import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AiPromptComposer from '../../components/AiPromptComposer';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import {
  AI_PROMPT_STORAGE_KEY,
  savePromptToSession,
  saveAttachmentsToSession,
  saveContactToSession,
  loadContactFromSession,
  loadPromptFromSession,
} from '../../utils/aiPromptStorage';
import {
  buildAttachmentsFromFileList,
  removeAttachmentById,
  MAX_PROMPT_ATTACHMENTS,
  MAX_PROMPT_ATTACHMENT_SIZE,
} from '../../utils/aiPromptAttachments';
import usePromptDraftController from '../../hooks/usePromptDraftController';
import { setAgentHandoff, getAgentHandoff } from '../../utils/aiAgentHandoff';
import getApiBaseUrl from '../../utils/apiBaseUrl';
import readJsonResponse from '../../utils/readJsonResponse';
import { navigateToCheckoutOverlay } from '../../utils/checkoutOverlayNavigation';
import { hasPendingPostSignupJourney } from '../../utils/postSignupJourney';
import '../AiBotBuilder/AiBotBuilder.css';
import '../AiBotBuilder/AiBotInputArea.css';
import useSalesFunnelViewportGuard from './useSalesFunnelViewportGuard';
import './SalesFunnelViewportGuard.css';
import './SalesAiPromptPage.css';

const PROMPT_MESSAGING = {
  title: "You're in—let's build your strategy 🚀",
  subtitle: "Describe the strategy you want and we'll build your bot preview so you can backtest, tweak, or launch next.",
  subtitleWithName: (name) => `${name}, describe the strategy you want and we'll build your bot preview so you can backtest, tweak, or launch next.`,
  placeholderExamples: null,
};
const PROMPT_DESTINATION_PATH = '/checkout/starter-plan';

const PROMPT_ANALYTICS_SOURCE = 'sales_paywall_pre_ai_prompt';

const SALES_PAGE_EXPERIMENT_PARAM_KEYS = [
  '$feature/sales_page_signup_framing_v4_variant',
  '$feature_flag_sales_page_signup_framing_v4_variant',
  '$feature/sales_page_signup_framing_v5_variant',
  '$feature_flag_sales_page_signup_framing_v5_variant',
  '$feature/google_search_signup_experience_v1_variant',
  '$feature_flag_google_search_signup_experience_v1_variant',
  '$feature/google_search_signup_experience_v2_variant',
  '$feature_flag_google_search_signup_experience_v2_variant',
  '$feature/google_search_auth_first_personalization_2026_08_variant',
  '$feature_flag_google_search_auth_first_personalization_2026_08_variant',
  'feature_flag',
  'experiment_name',
  'experiment_variant',
  'sales_page_signup_framing_v4_experiment_name',
  'sales_page_signup_framing_v5_experiment_name',
  'sales_page_signup_framing_variant',
  'sales_page_signup_framing_v4_variant',
  'sales_page_signup_framing_v4_route_variant',
  'sales_page_signup_framing_v5_variant',
  'sales_page_signup_framing_v5_route_variant',
  'google_search_signup_experience_v1_experiment_name',
  'google_search_signup_experience_v1_variant',
  'google_search_signup_experience_v2_experiment_name',
  'google_search_signup_experience_v2_variant',
  'google_search_auth_first_personalization_2026_08_experiment_name',
  'google_search_auth_first_personalization_2026_08_variant',
  'google_search_lp_job',
  'sales_page_form_variant',
  'sales_page_framing_variant',
  'sales_page_design_variant',
];

const getSalesPageExperimentProps = (leadInfo = {}) => (
  SALES_PAGE_EXPERIMENT_PARAM_KEYS.reduce((props, key) => {
    if (leadInfo[key]) {
      props[key] = leadInfo[key];
    }
    return props;
  }, {})
);

const isSameLeadInfo = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    (a.name || '').trim() === (b.name || '').trim() &&
    (a.email || '').trim() === (b.email || '').trim() &&
    (a.phone || '').trim() === (b.phone || '').trim() &&
    (a.leadTransactionId || null) === (b.leadTransactionId || null) &&
    (a.customerId || null) === (b.customerId || null) &&
    (a.variant || '') === (b.variant || '') &&
    (a.howFoundUs || '') === (b.howFoundUs || '') &&
    (a.howFoundUsOtherText || '') === (b.howFoundUsOtherText || '') &&
    SALES_PAGE_EXPERIMENT_PARAM_KEYS.every((key) => (a[key] || '') === (b[key] || ''))
  );
};

const appendLeadInfoParams = (params, leadInfo, promptVariant) => {
  if (leadInfo.leadTransactionId) {
    params.set('leadId', leadInfo.leadTransactionId);
  }
  if (leadInfo.name) {
    params.set('name', leadInfo.name);
  }
  if (leadInfo.email) {
    params.set('email', leadInfo.email);
  }
  if (leadInfo.phone) {
    params.set('phone', leadInfo.phone);
  }
  if (promptVariant) {
    params.set('variant', promptVariant);
  }
  if (leadInfo.howFoundUs) {
    params.set('howFoundUs', leadInfo.howFoundUs);
  }
  if (leadInfo.howFoundUsOtherText) {
    params.set('howFoundUsOtherText', leadInfo.howFoundUsOtherText);
  }
  SALES_PAGE_EXPERIMENT_PARAM_KEYS.forEach((key) => {
    if (leadInfo[key]) {
      params.set(key, leadInfo[key]);
    }
  });
};

export default function SalesAiPromptPage() {
  const location = useLocation();
  const navigate = useNavigate();
  useSalesFunnelViewportGuard();

  // 🔐 LEAD AUTO-LOGIN: Get unified auth context for seamless authentication
  const { handleCustomJwtAuth, isAuthenticated, getApiToken } = useUnifiedAuth();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const promptFromQuery = useMemo(() => (searchParams.get('prompt') || '').trim(), [searchParams]);
  const shouldAutoSkipPromptFromQuery = searchParams.get('autoGenerate') === 'true';
  const parsedLeadInfo = useMemo(() => {
    const rawName = (searchParams.get('name') || '').trim();
    const rawEmail = (searchParams.get('email') || '').trim();
    const rawPhone = (searchParams.get('phone') || '').trim();
    return {
      name: rawName,
      email: rawEmail,
      phone: rawPhone,
      leadTransactionId: searchParams.get('leadId') || null,
      variant: searchParams.get('variant') || null,
      howFoundUs: searchParams.get('howFoundUs') || '',
      howFoundUsOtherText: searchParams.get('howFoundUsOtherText') || searchParams.get('howFoundUsDetails') || '',
      '$feature/sales_page_signup_framing_v4_variant': searchParams.get('$feature/sales_page_signup_framing_v4_variant') || '',
      $feature_flag_sales_page_signup_framing_v4_variant: searchParams.get('$feature_flag_sales_page_signup_framing_v4_variant') || '',
      '$feature/sales_page_signup_framing_v5_variant': searchParams.get('$feature/sales_page_signup_framing_v5_variant') || '',
      $feature_flag_sales_page_signup_framing_v5_variant: searchParams.get('$feature_flag_sales_page_signup_framing_v5_variant') || '',
      '$feature/google_search_signup_experience_v1_variant': searchParams.get('$feature/google_search_signup_experience_v1_variant') || '',
      $feature_flag_google_search_signup_experience_v1_variant: searchParams.get('$feature_flag_google_search_signup_experience_v1_variant') || '',
      '$feature/google_search_signup_experience_v2_variant': searchParams.get('$feature/google_search_signup_experience_v2_variant') || '',
      $feature_flag_google_search_signup_experience_v2_variant: searchParams.get('$feature_flag_google_search_signup_experience_v2_variant') || '',
      '$feature/google_search_auth_first_personalization_2026_08_variant': searchParams.get('$feature/google_search_auth_first_personalization_2026_08_variant') || '',
      $feature_flag_google_search_auth_first_personalization_2026_08_variant: searchParams.get('$feature_flag_google_search_auth_first_personalization_2026_08_variant') || '',
      feature_flag: searchParams.get('feature_flag') || '',
      experiment_name: searchParams.get('experiment_name') || '',
      experiment_variant: searchParams.get('experiment_variant') || '',
      sales_page_signup_framing_v4_experiment_name: searchParams.get('sales_page_signup_framing_v4_experiment_name') || '',
      sales_page_signup_framing_v5_experiment_name: searchParams.get('sales_page_signup_framing_v5_experiment_name') || '',
      sales_page_signup_framing_variant: searchParams.get('sales_page_signup_framing_variant') || '',
      sales_page_signup_framing_v4_variant: searchParams.get('sales_page_signup_framing_v4_variant') || '',
      sales_page_signup_framing_v4_route_variant: searchParams.get('sales_page_signup_framing_v4_route_variant') || '',
      sales_page_signup_framing_v5_variant: searchParams.get('sales_page_signup_framing_v5_variant') || '',
      sales_page_signup_framing_v5_route_variant: searchParams.get('sales_page_signup_framing_v5_route_variant') || '',
      google_search_signup_experience_v1_experiment_name: searchParams.get('google_search_signup_experience_v1_experiment_name') || '',
      google_search_signup_experience_v1_variant: searchParams.get('google_search_signup_experience_v1_variant') || '',
      google_search_signup_experience_v2_experiment_name: searchParams.get('google_search_signup_experience_v2_experiment_name') || '',
      google_search_signup_experience_v2_variant: searchParams.get('google_search_signup_experience_v2_variant') || '',
      google_search_auth_first_personalization_2026_08_experiment_name: searchParams.get('google_search_auth_first_personalization_2026_08_experiment_name') || '',
      google_search_auth_first_personalization_2026_08_variant: searchParams.get('google_search_auth_first_personalization_2026_08_variant') || '',
      google_search_lp_job: searchParams.get('google_search_lp_job') || '',
      sales_page_form_variant: searchParams.get('sales_page_form_variant') || '',
      sales_page_framing_variant: searchParams.get('sales_page_framing_variant') || '',
      sales_page_design_variant: searchParams.get('sales_page_design_variant') || '',
      // 🔐 Include autoLoginToken for seamless authentication
      autoLoginToken: searchParams.get('autoLoginToken') || null,
    };
  }, [searchParams]);

  // 🔐 LEAD AUTO-LOGIN: Process auto-login token if present
  // This authenticates the lead without requiring Auth0 login
  useEffect(() => {
    const token = parsedLeadInfo.autoLoginToken;
    if (token && !isAuthenticated) {
      console.log('[SalesAiPromptPage] 🔐 Processing lead auto-login token');
      setIsProcessingAutoLogin(true);

      // Attempt to authenticate with the JWT token
      handleCustomJwtAuth(token).then((success) => {
        if (success) {
          console.log('[SalesAiPromptPage] ✅ Lead auto-login successful');

          // Remove token from URL for security (prevent token leakage via browser history/sharing)
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('autoLoginToken');
          window.history.replaceState({}, '', newUrl.toString());
        } else {
          console.warn('[SalesAiPromptPage] ⚠️ Lead auto-login token validation failed');
        }
      }).finally(() => {
        setIsProcessingAutoLogin(false);
      });
    }
  }, [parsedLeadInfo.autoLoginToken, handleCustomJwtAuth, isAuthenticated]);

  const [leadInfo, setLeadInfo] = useState(parsedLeadInfo);
  const leadInfoRef = useRef(parsedLeadInfo);
  const [submitError, setSubmitError] = useState('');
  const [isForwarding, setIsForwarding] = useState(false);
  const [isProcessingAutoLogin, setIsProcessingAutoLogin] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState('');
  const attachmentInputRef = useRef(null);
  const lastSyncedQueryRef = useRef('');

  const {
    promptValue: prompt,
    setPromptValue: setPrompt,
    clearPromptValue,
    restoredAttachmentWarning,
    registerAttachmentsMeta,
  } = usePromptDraftController({
    isRefinement: false,
  });

  const currentMessaging = PROMPT_MESSAGING;

  const utmData = useMemo(() => getUtmParamsForApi(), [location.search]);
  const isPostSignupCheckoutDestination =
    searchParams.get('postSignupCheckoutVariant') === 'test';

  // Check if there's an existing prompt (from homepage handoff) and skip this page
  // User already typed their prompt on the homepage, no need to ask again
  useEffect(() => {
    const performAutoSkip = () => {
      // Post-signup routing owns this transition. This page can remain mounted
      // as the checkout backdrop, where auto-skip would otherwise replace the
      // experiment checkout URL and discard its destination and exposure data.
      if (hasPendingPostSignupJourney() || isPostSignupCheckoutDestination) return;

      const handoff = getAgentHandoff();
      const sessionPrompt = loadPromptFromSession();
      const queryPrompt = shouldAutoSkipPromptFromQuery ? promptFromQuery : '';
      const existingPrompt = handoff?.prompt || sessionPrompt || queryPrompt;

      if (existingPrompt && existingPrompt.trim()) {
        console.log('[SalesAiPromptPage] Found existing prompt, skipping prompt page:', existingPrompt.substring(0, 50));
        if (queryPrompt && !sessionPrompt) {
          savePromptToSession(promptFromQuery);
        }

        // Determine the variant for analytics
        const experimentContext = window.__experimentContext || {};
        const promptVariant = parsedLeadInfo.variant || experimentContext.experiment_variant || 'control';

        // Log analytics for the skip
        pushEvent({
          event: ANALYTICS_EVENTS.SALES_AI_PROMPT_SUBMITTED,
          source: 'sales_paywall_pre_ai_prompt_auto_skip',
          variant: promptVariant,
          prompt_length: existingPrompt.trim().length,
          skip_reason: 'existing_prompt_from_homepage',
          ...utmData,
        });

        const destinationParams = new URLSearchParams();
        destinationParams.set('redirect', 'starter_plan');
        appendLeadInfoParams(destinationParams, parsedLeadInfo, promptVariant);

        const updatedHandoff = {
          ...handoff,
          prompt: existingPrompt,
        };
        setAgentHandoff(updatedHandoff);
        const destinationUrl = `${PROMPT_DESTINATION_PATH}?${destinationParams.toString()}`;
        console.log(`[SalesAiPromptPage] Redirecting to checkout: ${destinationUrl}`);
        navigate(destinationUrl, { replace: true });
      }
    };

    performAutoSkip();
  }, [
    navigate,
    isPostSignupCheckoutDestination,
    parsedLeadInfo,
    promptFromQuery,
    shouldAutoSkipPromptFromQuery,
    utmData,
  ]);

  useEffect(() => {
    lastSyncedQueryRef.current = location.search || '';
  }, [location.search]);

  const updateUrlWithContact = useCallback(
    (contact) => {
      if (!contact) return;
      const params = new URLSearchParams(location.search);
      let changed = false;

      const assignParam = (key, value) => {
        const trimmed = (value || '').trim();
        const current = params.get(key) || '';
        if (trimmed) {
          if (current !== trimmed) {
            params.set(key, trimmed);
            changed = true;
          }
        } else if (current) {
          params.delete(key);
          changed = true;
        }
      };

      assignParam('name', contact.name);
      assignParam('email', contact.email);
      assignParam('phone', contact.phone);
      assignParam('howFoundUs', contact.howFoundUs);
      assignParam('howFoundUsOtherText', contact.howFoundUsOtherText);
      SALES_PAGE_EXPERIMENT_PARAM_KEYS.forEach((key) => {
        assignParam(key, contact[key]);
      });

      const currentLeadId = params.get('leadId');
      if (contact.leadTransactionId) {
        if (currentLeadId !== contact.leadTransactionId) {
          params.set('leadId', contact.leadTransactionId);
          changed = true;
        }
      } else if (currentLeadId) {
        params.delete('leadId');
        changed = true;
      }

      const currentVariant = params.get('variant');
      const targetVariant =
        typeof contact.variant === 'string' && contact.variant.trim().length > 0
          ? contact.variant.trim()
          : null;
      if (targetVariant) {
        if (currentVariant !== targetVariant) {
          params.set('variant', targetVariant);
          changed = true;
        }
      } else if (currentVariant) {
        params.delete('variant');
        changed = true;
      }

      if (!changed) {
        return;
      }

      const nextSearch = params.toString();
      const nextSearchWithPrefix = nextSearch ? `?${nextSearch}` : '';
      if (
        nextSearchWithPrefix === lastSyncedQueryRef.current ||
        nextSearchWithPrefix === (location.search || '')
      ) {
        return;
      }
      lastSyncedQueryRef.current = nextSearchWithPrefix;
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    const nextLeadInfo = {
      ...parsedLeadInfo,
      variant: parsedLeadInfo.variant?.trim() || null,
    };

    if (!isSameLeadInfo(leadInfoRef.current, nextLeadInfo)) {
      setLeadInfo(nextLeadInfo);
      saveContactToSession(nextLeadInfo);
    }
  }, [location.search, parsedLeadInfo]);

  useEffect(() => {
    const experimentContext = window.__experimentContext || {};
    const promptVariant =
      leadInfo?.variant || experimentContext.experiment_variant || 'control';
    const experimentName =
      experimentContext.experiment_name ||
      (leadInfo?.variant ? 'sales_page_paywall_pre_ai_prompt' : undefined);
    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page: 'sales_paywall_pre_ai_prompt_stage',
      source: location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      variant: promptVariant,
      ...(experimentName ? { experiment: experimentName } : {}),
      ...utmData,
      ...experimentContext,
    });
  }, [leadInfo?.variant, location.pathname, utmData]);

  const sanitizedFirstName = useMemo(() => {
    if (!leadInfo?.name) return null;
    const [first] = leadInfo.name.split(/\s+/);
    if (!first) return null;
    return first.length > 32 ? first.slice(0, 32) : first;
  }, [leadInfo?.name]);

  useEffect(() => {
    leadInfoRef.current = leadInfo;
  }, [leadInfo]);

  useEffect(() => {
    if (!leadInfo) return;
    updateUrlWithContact(leadInfo);
    saveContactToSession(leadInfo);
  }, [leadInfo, updateUrlWithContact]);

  useEffect(() => {
    const stored = loadContactFromSession();
    if (!stored || !stored.email) {
      return;
    }
    const normalized = {
      name: (stored.name || '').trim(),
      email: (stored.email || '').trim(),
      phone: (stored.phone || '').trim(),
      leadTransactionId: stored.leadTransactionId || null,
      customerId: stored.customerId || null,
      variant: (stored.variant || '').trim() || null,
      howFoundUs: (stored.howFoundUs || '').trim(),
      howFoundUsOtherText: (stored.howFoundUsOtherText || stored.howFoundUsDetails || '').trim(),
    };
    SALES_PAGE_EXPERIMENT_PARAM_KEYS.forEach((key) => {
      normalized[key] = (stored[key] || parsedLeadInfo[key] || '').trim();
    });

    setLeadInfo((prev) => {
      const base = prev || leadInfoRef.current || {};
      if (isSameLeadInfo(base, normalized)) {
        return base;
      }
      return {
        ...base,
        ...normalized,
      };
    });

    updateUrlWithContact({
      ...(leadInfoRef.current || {}),
      ...normalized,
    });
  }, [parsedLeadInfo, updateUrlWithContact]);

  const handleContactInfoChange = useCallback(
    (payload = {}) => {
      const current = leadInfoRef.current || leadInfo || {};
      const normalized = {
        name: typeof payload.name === 'string' ? payload.name.trim() : current.name || '',
        email: typeof payload.email === 'string' ? payload.email.trim() : current.email || '',
        phone: typeof payload.phone === 'string' ? payload.phone.trim() : current.phone || '',
        leadTransactionId:
          payload.leadTransactionId || current.leadTransactionId || null,
        customerId: payload.customerId || current.customerId || null,
        variant:
          typeof payload.variant === 'string' && payload.variant.trim().length > 0
            ? payload.variant.trim()
            : current.variant || null,
        howFoundUs:
          typeof payload.howFoundUs === 'string'
            ? payload.howFoundUs.trim()
            : current.howFoundUs || '',
        howFoundUsOtherText:
          typeof payload.howFoundUsOtherText === 'string'
            ? payload.howFoundUsOtherText.trim()
            : current.howFoundUsOtherText || '',
      };

      if (!isSameLeadInfo(current, normalized)) {
        setLeadInfo((prev) => ({
          ...prev,
          ...normalized,
        }));
      }

      // persistence handled by leadInfo effect
    },
    [leadInfo],
  );

  const normalizeAttachmentsForAgent = useCallback((files = []) => {
    return (files || [])
      .map((attachment) => {
        const base64 = attachment?.content || attachment?.base64 || null;
        return {
          id: attachment.id || `att_${Math.random().toString(36).slice(2, 10)}`,
          name: attachment.name || 'attachment',
          mimeType: attachment.type || attachment.mimeType || 'application/octet-stream',
          sizeBytes: attachment.size || attachment.sizeBytes || 0,
          base64,
        };
      })
      .filter((attachment) => Boolean(attachment.base64));
  }, []);

  const handleAttachmentsSelected = useCallback(
    async (event) => {
      const fileList = event.target.files;
      if (!fileList || fileList.length === 0) {
        return;
      }

      const { attachments: newAttachments, errors } = await buildAttachmentsFromFileList(
        fileList,
        attachments.length,
        {
          maxFiles: MAX_PROMPT_ATTACHMENTS,
          maxSize: MAX_PROMPT_ATTACHMENT_SIZE,
        },
      );

      if (errors.tooManyFiles) {
        setAttachmentError(`Maximum ${MAX_PROMPT_ATTACHMENTS} files allowed`);
        setTimeout(() => setAttachmentError(''), 5000);
        event.target.value = '';
        return;
      }

      if (errors.oversizedFiles.length > 0) {
        const sizeMB = Math.round(errors.oversizedFiles[0]?.size / (1024 * 1024));
        setAttachmentError(`File too large (${sizeMB}MB). Maximum size is 100MB.`);
        setTimeout(() => setAttachmentError(''), 5000);
      }

      if (newAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...newAttachments]);
      }

      event.target.value = '';
    },
    [attachments.length],
  );

  const handleRemoveAttachment = useCallback((attachmentId) => {
    setAttachments((prev) => removeAttachmentById(prev, attachmentId));
  }, []);

  const handlePromptSubmit = useCallback(async () => {
    const trimmed = prompt.trim();
    const experimentContext = window.__experimentContext || {};
    const promptVariant =
      leadInfo.variant || experimentContext.experiment_variant || 'control';
    const salesPageExperimentProps = getSalesPageExperimentProps(leadInfo);
    if (!trimmed) {
      setSubmitError('Please describe the strategy you want to build before continuing.');
      pushEvent({
        event: ANALYTICS_EVENTS.SALES_AI_PROMPT_INVALID,
        reason: 'empty_prompt',
        source: PROMPT_ANALYTICS_SOURCE,
        variant: promptVariant,
        ...utmData,
        ...salesPageExperimentProps,
        ...experimentContext,
      });
      return;
    }

    setSubmitError('');
    setIsForwarding(true);

    savePromptToSession(trimmed);
    saveAttachmentsToSession(attachments);

    if (isAuthenticated && getApiToken) {
      getApiToken()
        .then((token) => (
          token
            ? fetch(`${getApiBaseUrl()}/users/prompt`, {
                method: 'POST',
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ prompt: trimmed }),
              }).then((response) => readJsonResponse(response, {
                errorMessage: 'Prompt save failed',
                allowEmpty: true,
              }))
            : null
        ))
        .catch(err => console.warn('[SalesAiPromptPage] Prompt save failed:', err));
    }

    try {
      pushEvent({
        event: ANALYTICS_EVENTS.SALES_AI_PROMPT_SUBMITTED,
        source: PROMPT_ANALYTICS_SOURCE,
        variant: promptVariant,
        prompt_length: trimmed.length,
        prompt_text: trimmed.substring(0, 500),
        attachment_count: attachments.length,
        email: leadInfo.email || '',
        lead_transaction_id: leadInfo.leadTransactionId || null,
        ...utmData,
        ...salesPageExperimentProps,
        ...experimentContext,
      });

      const normalizedAttachments = normalizeAttachmentsForAgent(attachments);
      const handoffPayload = {
        prompt: trimmed,
        attachments: normalizedAttachments.length ? normalizedAttachments : undefined,
        source: PROMPT_ANALYTICS_SOURCE,
      };
      setAgentHandoff(handoffPayload);

      const destinationParams = new URLSearchParams();
      destinationParams.set('redirect', 'starter_plan');
      appendLeadInfoParams(destinationParams, leadInfo, promptVariant);

      clearPromptValue();
      const destinationUrl = `${PROMPT_DESTINATION_PATH}?${destinationParams.toString()}`;
      console.log(`[SalesAiPromptPage] Redirecting to checkout: ${destinationUrl}`);
      navigateToCheckoutOverlay(navigate, destinationUrl, location, { replace: true });
    } catch (error) {
      console.error('[SalesAiPromptPage] Failed to forward prompt to AI Agent', error);
      setSubmitError('We ran into an issue opening the assistant. Please try again.');
    } finally {
      setIsForwarding(false);
    }
  }, [
    attachments,
    clearPromptValue,
    getApiToken,
    isAuthenticated,
    leadInfo,
    location,
    navigate,
    normalizeAttachmentsForAgent,
    prompt,
    utmData,
  ]);

  const handlePromptKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey && !isForwarding) {
        event.preventDefault();
        handlePromptSubmit();
      }
    },
    [handlePromptSubmit, isForwarding],
  );

  useEffect(() => {
    const meta = attachments.map((file) => ({
      id: file.id,
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    registerAttachmentsMeta(meta);
  }, [attachments, registerAttachmentsMeta]);

  // Back button: lets users return to /sales-choose if they're stuck.
  // Rob (Apr 9): "people freeze on the sales ai prompt page bc they dont know
  // what to write" — back button gives them a way out to pick a proven strategy.
  const handleBackToChoose = () => {
    pushEvent({ event: 'sales_ai_prompt_back_clicked' });
    navigate(`/sales-choose${location.search}`);
  };

  return (
    <div className="sales-ai-prompt-page">
      <div className="ai-bot-builder-wrapper ai-bot-builder-page-wide">
        <div className="ai-bot-builder-container initial-state">
          {/* Back button — lets stuck users return to /sales-choose */}
          <button
            type="button"
            onClick={handleBackToChoose}
            className="sales-ai-back-button"
            aria-label="Back to choose page"
          >
            ← Back
          </button>
          <div className="sales-ai-hero">
            <h1 className="hero-title">{currentMessaging.title}</h1>
            <p className="hero-subtitle">
              {sanitizedFirstName
                ? currentMessaging.subtitleWithName(sanitizedFirstName)
                : currentMessaging.subtitle}
            </p>
          </div>

          <div className="ai-bot-input-area">
            <div className="aibot-input-area-container">
              <AiPromptComposer
                value={prompt}
                onChange={(value) => setPrompt(value)}
                onKeyDown={handlePromptKeyDown}
                onSubmit={handlePromptSubmit}
                canSubmit={!!prompt.trim() && !isForwarding && !isProcessingAutoLogin}
                isProcessing={isForwarding}
                isRefinement={false}
                sendButtonAriaLabel="Send to AI Agent"
                attachmentsConfig={{
                  enabled: true,
                  inputRef: attachmentInputRef,
                  onAttachmentsSelected: handleAttachmentsSelected,
                  attachments,
                  onRemoveAttachment: handleRemoveAttachment,
                  attachmentButtonDisabled: isForwarding,
                }}
                creditsConfig={{ show: false }}
                textareaProps={{ rows: 6 }}
                placeholderExamples={currentMessaging.placeholderExamples}
              />
              {restoredAttachmentWarning ? (
                <div className="sales-ai-warning">{restoredAttachmentWarning}</div>
              ) : null}
              {attachmentError ? <div className="sales-ai-error">{attachmentError}</div> : null}
              {submitError ? <div className="sales-ai-error">{submitError}</div> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
